/**
 * Brackets (konsoll): SK 564/770 AL with spigot, and the SKD 1250 AL diagonal.
 *
 * Both hook a cup group with a spring locking catch and end in a stub — a Ø48
 * tube one node tall with a cup group at the bracket's level and a Ø38 spigot
 * on top. The stub's cups, and whatever stands on the spigot, are drawn by the
 * standards pass through the mounted leg; this pass draws the stub tube, the
 * spigot, the hooks and the arm (SK) or the diagonal (SKD). The SKD's arm is a
 * normal beam, drawn and booked with the beams.
 */

import { Group, Mesh } from "three";
import {
  BRACKET_STUB,
  bracketName,
  CFG,
  KL,
  KR,
  nodeY,
  SKD_HOOK_LEVELS_BELOW,
  TAPP,
} from "../../data/haki.ts";
import type { Bracket, Point } from "../../model/types.ts";
import { G } from "../geometries.ts";
import { M } from "../materials.ts";
import { boxAt, tubeBetween, V, yawOf } from "../threeHelpers.ts";
import type { RenderContext } from "./context.ts";

/** How far the hook castings sit from the standard's axis, metres. */
const HOOK_OFFSET = 0.055;

/** Where the arm or diagonal tube starts, clear of the hook castings. */
const TUBE_START = 0.07;

/** The SK stub reaches this far below its cup level, to the bottom casting. */
const SK_STUB_BELOW_NODE = 0.03;

/** The SKD's vertical part bends into the diagonal this far above its cup level. */
const SKD_BEND_ABOVE_NODE = 0.07;

/** Sphere radius that matches the Ø48 tube, as a scale of `G.sph`. */
const ELBOW_SCALE = 0.024 / 0.055;

type Frame = Readonly<{ ux: number; uz: number; rot: number; yNode: number }>;

function frameOf(bracket: Bracket): Frame {
  const dx = bracket.b.x - bracket.a.x;
  const dz = bracket.b.z - bracket.a.z;
  const length = Math.hypot(dx, dz);
  const ux = dx / length;
  const uz = dz / length;

  return { ux, uz, rot: yawOf(ux, uz), yNode: nodeY(bracket.p) };
}

/** Hook castings latching the cup group at `yNode`, and the spring-lock nub. */
function renderHooks(group: Group, at: Point, frame: Frame, yNode: number, bothCups: boolean): void {
  const { ux, uz, rot } = frame;
  const hx = at.x + ux * HOOK_OFFSET;
  const hz = at.z + uz * HOOK_OFFSET;

  group.add(boxAt(hx, yNode + KR + 0.004, hz, 0.06, 0.05, 0.06, M.cast, rot));
  if (bothCups) {
    group.add(boxAt(hx, yNode + KL, hz, 0.05, 0.04, 0.05, M.cast, rot));
  }
  group.add(boxAt(at.x + ux * 0.085, yNode + KR + 0.036, at.z + uz * 0.085, 0.028, 0.016, 0.04, M.cast, rot));
}

/** The outer tube from `bottom` up to the node above the bracket, with the spigot on top. */
function renderStub(group: Group, b: Point, frame: Frame, bottom: number): void {
  const top = frame.yNode + BRACKET_STUB;
  group.add(tubeBetween(V(b.x, bottom, b.z), V(b.x, top, b.z), G.tube48, M.alu));

  const spigot = new Mesh(G.cyl, M.aluD);
  spigot.scale.set(0.019, TAPP, 0.019);
  spigot.position.set(b.x, top + TAPP / 2, b.z);
  spigot.castShadow = true;
  group.add(spigot);
}

/** Two Ø34 members at the cup heights, latching both cups, into the stub. */
function renderSk(group: Group, bracket: Bracket, frame: Frame): void {
  const { ux, uz, yNode } = frame;
  const start = V(bracket.a.x + ux * TUBE_START, 0, bracket.a.z + uz * TUBE_START);
  const end = V(bracket.b.x, 0, bracket.b.z);

  group.add(tubeBetween(start.clone().setY(yNode + KR), end.clone().setY(yNode + KR), G.tube34, M.alu));
  group.add(tubeBetween(start.clone().setY(yNode + KL), end.clone().setY(yNode + KL), G.tube34, M.alu));
  renderHooks(group, bracket.a, frame, yNode, true);
  renderStub(group, bracket.b, frame, yNode - SK_STUB_BELOW_NODE);

  const foot = new Mesh(G.collar, M.cast);
  foot.position.set(bracket.b.x, yNode - SK_STUB_BELOW_NODE + 0.02, bracket.b.z);
  foot.castShadow = true;
  group.add(foot);
}

/**
 * One bent Ø48 tube: the vertical part is the stub itself, bending just above
 * the cup level into a diagonal that hooks the standard `SKD_HOOK_LEVELS_BELOW`
 * levels down.
 */
function renderSkd(group: Group, bracket: Bracket, frame: Frame): void {
  const { ux, uz, yNode } = frame;
  const hookNode = nodeY(bracket.p - SKD_HOOK_LEVELS_BELOW);
  const bend = V(bracket.b.x, yNode + SKD_BEND_ABOVE_NODE, bracket.b.z);
  const hook = V(bracket.a.x + ux * TUBE_START, hookNode + KR, bracket.a.z + uz * TUBE_START);

  renderStub(group, bracket.b, frame, bend.y);
  group.add(tubeBetween(bend, hook, G.tube48, M.alu));

  const elbow = new Mesh(G.sph, M.alu);
  elbow.scale.setScalar(ELBOW_SCALE);
  elbow.position.copy(bend);
  elbow.castShadow = true;
  group.add(elbow);

  renderHooks(group, bracket.a, frame, hookNode, false);
}

function bookBracket(ctx: RenderContext, bracket: Bracket): void {
  const table = bracket.kind === "sk" ? CFG.bracket.sk : CFG.bracket.skd;
  const art = table.art[bracket.len];
  const kg = table.kg[bracket.len];

  ctx.bom.add("Brackets", bracketName(bracket.kind, bracket.len), art ?? "—", kg ?? 4);
}

export function renderBrackets(ctx: RenderContext): void {
  ctx.model.brackets.forEach((bracket, idx) => {
    const frame = frameOf(bracket);
    const group = new Group();

    if (bracket.kind === "sk") {
      renderSk(group, bracket, frame);
    } else {
      renderSkd(group, bracket, frame);
    }

    for (const child of group.children) {
      child.userData = { type: "bracket", idx };
      if (child instanceof Mesh) {
        ctx.pickElems.push(child);
      }
    }
    ctx.root.add(group);

    bookBracket(ctx, bracket);
  });
}
